import { DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { Breakpoint, SxProps, Theme } from './index';

/**
 * Appends and overwrites styles with the first defined style in lowest priority
 * If the style evaluates to falsey, it is not appended
 */
export const sxAppend = (
    ...styles: Array<SxProps<Theme> | false | undefined | null | 0 | ''>
): SxProps<Theme> =>
    styles.reduce(
        (previousStyles: SxProps<Theme>, newStyle) => ({
            ...previousStyles,
            ...(newStyle || {}),
        }),
        {}
    );

/** The idea for this came from https://stackoverflow.com/questions/68567118/react-material-ui-typography-max-number-of-lines
 * Documentation for the `-webkit-line-clamp` property can be found {@link https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-line-clamp here}.
 * {@link https://caniuse.com/?search=webkit-box Here} is a list of browsers that support the `-webkit-box` display property.
 * We took this approach because it is supported by various browsers, but if a better idea comes to light, then we definitely should revisit this.
 */
export const styleMaxLines = (
    maxLines?: number | Record<Breakpoint, number>
): SxProps => ({
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: maxLines,
});

export const styleNoScrollbar: SxProps = {
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
};

export const styleColorOverlay = (
    backgroundColor: string,
    overlayColor: string
): { background: string } => ({
    background: `linear-gradient(0deg, ${overlayColor} 0%, ${overlayColor} 100%), ${backgroundColor}`,
});

export const styleArrowBackIosCorrector = {
    position: 'relative',
};

/** Either a number or a breakpoint object with numbers */
export type ValueByBreakpoint<T = string | number> = T | Record<Breakpoint, T>;
export type NumberByBreakpoint = ValueByBreakpoint<number>;

export function getValueByBreakpoint<T = string | number>(
    value: T | ValueByBreakpoint<T>,
    breakpoint: Breakpoint
): T | undefined {
    if (typeof value === 'string' || typeof value === 'number') {
        return value as T;
    }
    return (value as Record<Breakpoint, T>)[breakpoint] as T;
}

/** Default padding for page layouts */
export const responsivePaddingDefault = {
    xs: DS_SPACING_UNITS.SIDE_SMALL,
    sm: DS_SPACING_UNITS.SIDE_SMALL,
    md: DS_SPACING_UNITS.SIDE_MEDIUM,
    lg: DS_SPACING_UNITS.SIDE_LARGE,
};
